import React, { useEffect, useState } from 'react';
import { Close as CloseIcon, InfoOutlined } from '@mui/icons-material';
import ApiService from '../../ApiService';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { getPaletteActionColor } from '../../theme';

export const EXPORT_TYPE_GROUPS: Record<string, string> = {
    dspimpressions: 'Campaign Performance',
    reachandfrequency: 'Campaign Performance',
    summaryappbyspend: 'Campaign Performance',
    technologysummary: 'Campaign Performance',
    offamazonconversions: 'Conversions & Attribution',
    reportedsales: 'Conversions & Attribution',
    amcattribution: 'Conversions & Attribution',
    hatreporting: 'HAT',
    shiftdailyctv: 'Shift Digital',
    shiftdailydigad: 'Shift Digital',
    shiftmonthlyspend: 'Shift Digital',
};

type ReportDetail = {
    key: string;
    name: string;
    cadence: string;
    period: string;
    contents: string;
};

export const REPORTS: ReportDetail[] = [
    {
        key: 'dspimpressions',
        name: 'DSP Impressions',
        cadence: 'Daily between 7-8am EST',
        period: 'Previous 7 Days of Daily metrics',
        contents:
            'Advertiser, Campaign, Order, Line Item, Creative level Impressions data with engagement data and downstream conversions roll ups.',
    },
    {
        key: 'reachandfrequency',
        name: 'Reach & Frequency',
        cadence: 'Daily between 7-8am EST',
        period: 'MTD Data',
        contents: 'Advertiser, Campaign, Order level Reach & Household Frequency data.',
    },
    {
        key: 'summaryappbyspend',
        name: 'Summary by App Spend',
        cadence: 'Daily between 7-8am EST',
        period: 'MTD Data',
        contents: 'Advertiser, Campaign, Order, Line Item level delivery by Property.',
    },
    {
        key: 'technologysummary',
        name: 'Technology Summary',
        cadence: 'Daily between 7-8am EST',
        period: 'MTD Data',
        contents: 'Advertiser, Campaign, Order, Line Item level Device data for Impressions served.',
    },
    {
        key: 'offamazonconversions',
        name: 'Off Amazon Conversions',
        cadence: 'Daily between 7-8am EST',
        period: 'Previous 7 Days of Daily metrics',
        contents:
            'Advertiser, Campaign, Order, Line Item, and Creative level Amazon Ad Tag Conversions from Impressions served. Broken out by the specific conversion types and actions setup.',
    },
    {
        key: 'reportedsales',
        name: 'Reported Sales',
        cadence: 'Daily between 7-8am EST',
        period: 'MTD + Previous 2 months Data',
        contents: 'Advertiser level Polk Reported Sales with Competitor sales and market share data.',
    },
    {
        key: 'amcattribution',
        name: 'AMC Attribution',
        cadence: 'Daily between 7-8am EST',
        period: 'MTD Data',
        contents: 'Advertiser, Campaign level Clean Room Measurement / Attribution.',
    },
    {
        key: 'hatreporting',
        name: 'HAT Reporting',
        cadence: 'Daily between 7-8am EST',
        period: 'MTD Data',
        contents: 'Advertiser level Ad Influenced website Traffic and Conversions for automotive dealers.',
    },
    {
        key: 'shiftdailyctv',
        name: 'Shift Daily CTV',
        cadence: 'Daily between 5-6am EST',
        period: 'Previous 2 Days of Daily metrics',
        contents: 'SHIFT OEM Styled Daily CTV Report for easy grab and merge of data for clients.',
    },
    {
        key: 'shiftdailydigad',
        name: 'Shift Daily DigAD',
        cadence: 'Daily between 5-6am EST',
        period: 'Previous 2 Days of Daily metrics',
        contents: 'SHIFT OEM Styled Daily Digital Ad Report for easy grab and merge of data for clients.',
    },
    {
        key: 'shiftmonthlyspend',
        name: 'Shift Monthly Spend',
        cadence: 'Monthly - 4th day of the month',
        period: 'Previous Month',
        contents: 'SHIFT OEM Styled Monthly Spend Report for easy grab and merge of data for clients.',
    },
];

const GROUP_BADGES: Record<string, string> = {
    'Conversions & Attribution': 'Amazon DSP Only',
    'Shift Digital': 'S3 Only',
};

const GROUP_ORDER = ['Campaign Performance', 'Conversions & Attribution', 'HAT', 'Shift Digital'];

type SelectReportsDialogProps = {
    open: boolean;
    onClose: () => void;
    agencyId: number;
    selectedReports: Set<string>;
    setSelectedReports: React.Dispatch<React.SetStateAction<Set<string>>>;
    onSave?: () => void;
    saveField?: 'sources' | 'sourcesOndemand';
};

const selectedRowBg = getPaletteActionColor('primary', 'selected');

export default function SelectReportsDialog({
    open,
    onClose,
    agencyId,
    selectedReports,
    setSelectedReports,
    onSave,
    saveField = 'sources',
}: SelectReportsDialogProps) {
    const [draftReports, setDraftReports] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (open) {
            setDraftReports(new Set(selectedReports));
        }
    }, [open]);

    const handleToggle = (key: string) => {
        setDraftReports((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const reportsByGroup = GROUP_ORDER.map((group) => ({
        group,
        badge: GROUP_BADGES[group],
        reports: REPORTS.filter((r) => EXPORT_TYPE_GROUPS[r.key] === group),
    }));

    return (
        <Dialog fullWidth={true} maxWidth="md" open={open} onClose={onClose}>
            <DialogTitle sx={{ m: 0, p: 2 }}>
                Select Reports
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: 12,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ px: 0, py: 0 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={draftReports.size === REPORTS.length}
                                        indeterminate={draftReports.size > 0 && draftReports.size < REPORTS.length}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setDraftReports(new Set(REPORTS.map((r) => r.key)));
                                            } else {
                                                setDraftReports(new Set());
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="subtitle2">Report Name</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="subtitle2">Report Period</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="subtitle2">Cadence</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="subtitle2">Contents</Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reportsByGroup.map(({ group, badge, reports }) => (
                                <>
                                    <TableRow key={`group-${group}`}>
                                        <TableCell
                                            colSpan={5}
                                            sx={{
                                                backgroundColor: '#f5f5f5',
                                                py: 1.5,
                                                px: 2,
                                                borderBottom: '1px solid #e0e0e0',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        {group}
                                                    </Typography>
                                                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                </Box>
                                                {badge && (
                                                    <Chip
                                                        label={badge}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {reports.map((report) => (
                                        <TableRow
                                            key={report.key}
                                            selected={draftReports.has(report.key)}
                                            sx={{
                                                '&.Mui-selected': {
                                                    backgroundColor: selectedRowBg,
                                                },
                                                '&.Mui-selected:hover': {
                                                    backgroundColor: selectedRowBg,
                                                },
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={draftReports.has(report.key)}
                                                    onChange={() => handleToggle(report.key)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{report.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {report.period}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {report.cadence}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {report.contents}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="outlined" color="primary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setSelectedReports(new Set(draftReports));
                        const joined = Array.from(draftReports).join(',');
                        const sources = saveField === 'sources' ? joined : undefined;
                        const sourcesOndemand = saveField === 'sourcesOndemand' ? joined : undefined;
                        ApiService.createExportSettings(agencyId, sources, sourcesOndemand).then(() => {
                            onSave?.();
                            onClose();
                        });
                    }}
                >
                    Confirm Selection
                </Button>
            </DialogActions>
        </Dialog>
    );
}
